
import React, { useState, useEffect } from 'react';
import { Auth as SupabaseAuth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getLogger } from '@/utils/logger';
import { Mail, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const log = getLogger({ module: 'SignInForm' });

export const SignInForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [showMagicLinkInput, setShowMagicLinkInput] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  
  useEffect(() => {
    // This listener is specific to the signin form to provide immediate feedback
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      log.info('SignInForm - Auth state changed:', event);
      
      if (event === 'SIGNED_IN' && session) {
        setIsSubmitting(false);
        toast.success('Signed in successfully');
        // Use a slight delay to ensure state is updated correctly
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 100);
      } else if (event === 'SIGNED_OUT') {
        setIsSubmitting(false);
      } else if (event === 'USER_UPDATED') {
        log.info('User was updated');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicLinkEmail) {
      toast.error('Please enter your email');
      return;
    }

    setSendingMagicLink(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: magicLinkEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        log.error('Magic link error:', error);
        toast.error(error.message);
      } else {
        toast.success('Magic link sent! Check your email.');
        setShowMagicLinkInput(false);
        setMagicLinkEmail('');
      }
    } catch (err) {
      log.error('Unexpected magic link error:', err);
      toast.error('Failed to send magic link');
    } finally {
      setSendingMagicLink(false);
    }
  };

  return (
    <div className={isSubmitting ? 'opacity-70 pointer-events-none' : ''}>
      <SupabaseAuth
        supabaseClient={supabase}
        view="sign_in"
        appearance={{
          theme: ThemeSupa,
          variables: {
            default: {
              colors: {
                brand: '#7C3AED',
                brandAccent: '#6D28D9',
                inputBackground: 'transparent',
                inputText: 'white',
                inputBorder: 'rgba(255, 255, 255, 0.1)',
                inputBorderHover: 'rgba(255, 255, 255, 0.2)',
                inputBorderFocus: '#7C3AED',
                inputPlaceholder: 'rgba(255, 255, 255, 0.5)',
              }
            }
          },
          className: {
            input: 'text-white bg-white/10 border border-white/10',
            button: 'bg-primary hover:bg-primary/90 opacity-100 disabled:opacity-50',
            label: 'text-white',
            container: 'space-y-4',
            message: 'text-sm text-red-500',
          }
        }}
        providers={[]}
        redirectTo={`${window.location.origin}/auth/callback`}
        onlyThirdPartyProviders={false}
        magicLink={false}
        showLinks={true}
        localization={{
          variables: {
            sign_in: {
              email_label: 'Email',
              password_label: 'Password',
              button_label: 'Sign In',
              loading_button_label: 'Signing In...',
              link_text: 'Already have an account? Sign in',
            },
            sign_up: {
              email_label: 'Email',
              password_label: 'Password',
              button_label: 'Sign Up',
              loading_button_label: 'Signing Up...',
              link_text: "Don't have an account? Sign up",
            }
          }
        }}
      />
      
      <div className="mt-6 pt-6 border-t border-white/10">
        {!showMagicLinkInput ? (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setShowMagicLinkInput(true)}
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors"
              type="button"
            >
              <Mail className="w-4 h-4" />
              Sign in with Magic Link
            </button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-white/50 hover:text-white/70" type="button">
                    <Info className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Get a one-time sign-in link sent to your email. No password needed!</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <form onSubmit={handleMagicLink} className="space-y-3">
            <input
              type="email"
              value={magicLinkEmail}
              onChange={(e) => setMagicLinkEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-md text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={sendingMagicLink}
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={sendingMagicLink}
                className="flex-1 bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingMagicLink ? 'Sending...' : 'Send Magic Link'}
              </button>
              <button
                type="button"
                onClick={() => setShowMagicLinkInput(false)}
                className="px-4 py-2 text-white/70 hover:text-white text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
